export const DOCTOR_MULTI_TOOTH_PENDING_CODE = "__multi_tooth_pending";

const ALL_DOCTOR_SURFACES = ["B", "O", "M", "D", "L"];
const SURFACE_ORDER = ["M", "O", "D", "B", "L", "I"];

function splitDoctorSurfaces(surface) {
  if (!surface) return [];
  const set = new Set(String(surface).toUpperCase().split("").filter((c) => SURFACE_ORDER.includes(c)));
  return SURFACE_ORDER.filter((c) => set.has(c));
}

// On-the-go (MV / is_informal bundle) teeth are owned by the "Ongoing"
// bundle layer, never by the green "Done" layer. A logged MV *visit* must
// NOT green the tooth — only the bundle ITEM reaching Completed does that,
// and a Completed item drops out of the open-bundle list below. Because a
// "done" draft row (seedItemFromSchedule) does not carry is_informal, we
// identify MV teeth by cross-referencing tooth_number against the open
// bundle items. Formal-plan teeth aren't in bundleItems, so their
// completed-visit → green behavior is untouched.
function openBundleToothSet(bundleItems = []) {
  const set = new Set();
  for (const row of bundleItems || []) {
    if (!row?.tooth_number || !row?.procedure_id) continue;
    // Bundle items are the OPEN procedures of the ongoing plan; a Completed
    // item is excluded upstream (Doctor.jsx builds bundleItems from the
    // still-open bundle procedures). Guard here too if a status leaks in.
    const status = String(row.status || "").toLowerCase();
    if (status === "completed") continue;
    set.add(String(row.tooth_number));
  }
  return set;
}

function normalizeStackProcedure(row) {
  if (!row || typeof row !== "object") return null;
  const surfaceCodes = splitDoctorSurfaces(row.surface ?? row.surfaces ?? null);
  const color = row.color || row.color_hex || row.procedure_color || "#94a3b8";
  return {
    procedure_name: row.procedure_name || row.name || "Procedure",
    status: row.status || "",
    assigned_doctor: row.assigned_doctor || row.doctor_name || "Unassigned",
    color,
    surface: surfaceCodes.length > 0 ? surfaceCodes.join("") : null,
    surface_codes: surfaceCodes,
  };
}

function collectDoctorProcedurePaintRows({
  chartMode,
  scheduled = [],
  dispositions = {},
  draftItems = [],
  activePlanItems = [],
  futureNeeds = [],
  bundleItems = [],
  procedureColorById = {},
} = {}) {
  const source = [];
  if (chartMode === "today") {
    const seenTn = new Set();
    const mvTeeth = openBundleToothSet(bundleItems);
    for (const row of scheduled || []) {
      if (!row?.tooth_number) continue;
      // An open on-the-go (MV) tooth is owned by the "Ongoing" bundle layer
      // below — don't ALSO paint it here from the scheduled feed, or the same
      // procedure shows up twice in the tooth's stack (once "Scheduled", once
      // "Ongoing"). Formal-plan teeth are never in mvTeeth, so they paint as
      // before. Mirrors the same guard on the draftItems loop.
      if (mvTeeth.has(String(row.tooth_number))) continue;
      const disp = row?.tn_id ? dispositions?.[row.tn_id] : null;
      if (disp?.kind === "not_today" || disp?.kind === "declined") continue;
      source.push({
        ...row,
        procedure_name: row.procedure_name || row.name,
        status: "Scheduled",
        color: procedureColorById[row.procedure_id],
      });
      if (row?.tn_id) seenTn.add(row.tn_id);
    }
    for (const row of draftItems || []) {
      if (!row?.tooth_number) continue;
      if ((row?.outcome || "done") !== "done") continue;
      if (row?.tn_id && seenTn.has(row.tn_id)) continue;
      // Suppress the green "Done" paint for a completed visit that belongs
      // to an OPEN on-the-go (MV) procedure — the "Ongoing" bundle layer
      // below owns that tooth's color until the item itself is Completed.
      // Formal-plan teeth are never in mvTeeth, so they green as before.
      if (mvTeeth.has(String(row.tooth_number))) continue;
      source.push({
        ...row,
        procedure_name: row.procedure_name || row.name,
        status: "Done",
        color: procedureColorById[row.procedure_id],
      });
    }
    // On-the-go bundle procedures paint on their tooth alongside today's work,
    // so the odontogram reflects in-progress multi-visit treatment. A
    // Completed bundle ITEM is not painted as "Ongoing" here — it drops out so
    // the tooth greens through the normal completed-work path like any other
    // finished procedure. Open items (Scheduled / In Progress / etc.) keep
    // their in-progress color even when they have one or more logged visits.
    for (const row of bundleItems || []) {
      if (!row?.tooth_number || !row?.procedure_id) continue;
      if (String(row.status || "").toLowerCase() === "completed") continue;
      source.push({
        ...row,
        procedure_name: row.procedure_name || row.name,
        status: "Ongoing",
        color: procedureColorById[row.procedure_id],
      });
    }
  } else if (chartMode === "tx_plan") {
    for (const row of activePlanItems || []) {
      if (isCancelledOrDeclined(row?.status)) continue;
      const teethArr = Array.isArray(row?.teeth)
        ? row.teeth
            .map((tooth) => (typeof tooth === "object" ? tooth?.tooth_number : tooth))
            .filter(Boolean)
        : [];
      if (teethArr.length > 1) {
        for (const toothNumber of teethArr) {
          source.push({
            ...row,
            tooth_number: toothNumber,
            surface: null,
            color: row.color || procedureColorById[row.procedure_id],
          });
        }
      } else if (row?.tooth_number) {
        source.push({
          ...row,
          color: row.color || procedureColorById[row.procedure_id],
        });
      }
    }
  } else if (chartMode === "future_need") {
    for (const row of futureNeeds || []) {
      if (!row?.tooth_number) continue;
      source.push({
        ...row,
        procedure_name: row.procedure_name || row.name,
        status: row.status || "Open",
        color: row.color || procedureColorById[row.procedure_id],
      });
    }
  }
  return source;
}

export function normalizeStackSummaryByTooth(rows = []) {
  const out = {};
  for (const row of rows || []) {
    if (!row?.tooth_number || !Number.isFinite(Number(row.stack_count)) || Number(row.stack_count) <= 1) continue;
    const fdi = String(row.tooth_number);
    if (out[fdi]) continue;
    const procedures = Array.isArray(row.stacked_procedures)
      ? row.stacked_procedures.map(normalizeStackProcedure).filter(Boolean)
      : [];
    const surfaceColumns = {};
    const wholeToothColumns = [];
    for (const proc of procedures) {
      if (proc.surface_codes.length === 0) {
        wholeToothColumns.push(proc);
        for (const surface of ALL_DOCTOR_SURFACES) {
          if (!surfaceColumns[surface]) surfaceColumns[surface] = [];
          surfaceColumns[surface].push(proc);
        }
        continue;
      }
      for (const surface of proc.surface_codes) {
        if (!surfaceColumns[surface]) surfaceColumns[surface] = [];
        surfaceColumns[surface].push(proc);
      }
    }
    out[fdi] = {
      stack_count: Number(row.stack_count),
      stacked_procedures: procedures,
      surface_columns: Object.fromEntries(
        Object.entries(surfaceColumns).map(([surface, items]) => [surface, items.slice(0, 4)]),
      ),
      whole_tooth_columns: wholeToothColumns.slice(0, 4),
    };
  }
  return out;
}

export function buildStackSummaryFromProcedureRows(rows = []) {
  const grouped = new Map();
  for (const row of rows || []) {
    if (!row?.tooth_number) continue;
    const fdi = String(row.tooth_number);
    const proc = normalizeStackProcedure(row);
    if (!proc) continue;
    if (!grouped.has(fdi)) grouped.set(fdi, []);
    grouped.get(fdi).push(proc);
  }

  const out = {};
  for (const [fdi, procedures] of grouped.entries()) {
    if (procedures.length <= 1) continue;
    const surfaceColumns = {};
    const wholeToothColumns = [];
    for (const proc of procedures) {
      if (proc.surface_codes.length === 0) {
        wholeToothColumns.push(proc);
        for (const surface of ALL_DOCTOR_SURFACES) {
          if (!surfaceColumns[surface]) surfaceColumns[surface] = [];
          surfaceColumns[surface].push(proc);
        }
        continue;
      }
      for (const surface of proc.surface_codes) {
        if (!surfaceColumns[surface]) surfaceColumns[surface] = [];
        surfaceColumns[surface].push(proc);
      }
    }
    out[fdi] = {
      stack_count: procedures.length,
      stacked_procedures: procedures,
      surface_columns: Object.fromEntries(
        Object.entries(surfaceColumns).map(([surface, items]) => [surface, items.slice(0, 4)]),
      ),
      whole_tooth_columns: wholeToothColumns.slice(0, 4),
    };
  }
  return out;
}

export function buildDoctorStackSummaryByTooth(options = {}) {
  return buildStackSummaryFromProcedureRows(collectDoctorProcedurePaintRows(options));
}

function isCancelledOrDeclined(status) {
  return ["cancelled", "declined"].includes(String(status || "").toLowerCase());
}

function compactLegendItem(row) {
  if (!row?.procedure_id && !row?.procedure_name) return null;
  const item = {
    procedure_id: row.procedure_id ?? row.id ?? row.procedure_name,
    procedure_name: row.procedure_name ?? row.name ?? null,
  };
  const category = row.procedure_category ?? row.category ?? null;
  if (category) item.procedure_category = category;
  return item;
}

function uniqueLegendItems(rows) {
  const out = [];
  const seen = new Set();
  for (const row of rows || []) {
    const item = compactLegendItem(row);
    if (!item) continue;
    const key = String(item.procedure_id ?? item.procedure_name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export function extractMissingFdiSet(chartState) {
  const out = new Set();
  const teeth = chartState?.teeth || {};
  for (const [fdi, tooth] of Object.entries(teeth)) {
    const findings = Array.isArray(tooth?.findings) ? tooth.findings : [];
    if (findings.some((finding) => String(finding?.finding_code || "").toLowerCase() === "missing")) {
      out.add(String(fdi));
    }
  }
  return out;
}

export function buildDoctorProcedureLegendItems({
  chartMode,
  scheduled = [],
  dispositions = {},
  draftItems = [],
  activePlanItems = [],
  futureNeeds = [],
  bundleItems = [],
} = {}) {
  if (chartMode === "today") {
    const rows = [];
    const seenTn = new Set();
    const mvTeeth = openBundleToothSet(bundleItems);
    for (const row of scheduled || []) {
      const disp = row?.tn_id ? dispositions?.[row.tn_id] : null;
      if (disp?.kind === "not_today" || disp?.kind === "declined") continue;
      rows.push(row);
      if (row?.tn_id) seenTn.add(row.tn_id);
    }
    for (const row of draftItems || []) {
      if ((row?.outcome || "done") !== "done") continue;
      if (row?.tn_id && seenTn.has(row.tn_id)) continue;
      // MV completed-visit rows are represented by the bundle layer below,
      // so don't let them seed a duplicate/"done" legend entry.
      if (row?.tooth_number && mvTeeth.has(String(row.tooth_number))) continue;
      rows.push(row);
    }
    for (const row of bundleItems || []) {
      if (!row?.procedure_id) continue;
      rows.push(row);
    }
    return uniqueLegendItems(rows);
  }

  if (chartMode === "tx_plan") {
    return uniqueLegendItems((activePlanItems || []).filter((row) => !isCancelledOrDeclined(row?.status)));
  }

  if (chartMode === "future_need") {
    return uniqueLegendItems(futureNeeds || []);
  }

  return [];
}

export function buildDoctorOverlayState({
  chartMode,
  scheduled = [],
  dispositions = {},
  draftItems = [],
  activePlanItems = [],
  futureNeeds = [],
  bundleItems = [],
  multiToothBuffer = [],
} = {}) {
  if (chartMode === "chart") return null;

  const source = collectDoctorProcedurePaintRows({
    chartMode,
    scheduled,
    dispositions,
    draftItems,
    activePlanItems,
    futureNeeds,
    bundleItems,
  });
  const teeth = {};
  const notesByFdi = new Map();
  for (const row of source) {
    const fdi = String(row.tooth_number);
    const surfaces = row.surface ? splitDoctorSurfaces(row.surface) : ALL_DOCTOR_SURFACES;
    if (surfaces.length === 0 || !row.procedure_id) continue;
    if (!teeth[fdi]) teeth[fdi] = { findings: [] };
    teeth[fdi].findings.push({ finding_code: `__doc_${row.procedure_id}`, surfaces });
    const note = String(row.notes || "").trim();
    if (chartMode === "future_need" && note) {
      const existing = notesByFdi.get(fdi) || [];
      if (!existing.includes(note)) notesByFdi.set(fdi, [...existing, note]);
    }
  }

  if (chartMode === "tx_plan") {
    for (const toothNumber of multiToothBuffer || []) {
      const fdi = String(toothNumber);
      if (!fdi) continue;
      if (!teeth[fdi]) teeth[fdi] = { findings: [] };
      teeth[fdi].findings.push({
        finding_code: DOCTOR_MULTI_TOOTH_PENDING_CODE,
        surfaces: ALL_DOCTOR_SURFACES,
      });
    }
  }

  if (chartMode === "future_need") {
    for (const [fdi, notes] of notesByFdi.entries()) {
      if (!teeth[fdi]) teeth[fdi] = { findings: [] };
      teeth[fdi].findings.push({ finding_code: "note", surfaces: [], notes: notes.join("\n") });
    }
  }

  return Object.keys(teeth).length > 0 ? { teeth } : null;
}
